import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';

export const serialCodeAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'GAME_CATALOG_API_BASE_URL',
});
attachAuthInterceptors(serialCodeAPI);

export interface GetSerialCodesRequest {
  page?: number;
  limit?: number;
  search?: string;
  game_id?: string;
  game_name?: string;
  operation_type?: string;
  status?: string;
  is_active?: boolean;
  created_by_username?: string;
  serial_code_name?: string;
  serial_code?: string;
  purchased?: boolean;
  all?: boolean;
}

export interface SerialCodeData {
  id: string;
  serial_code: string;
  serial_code_name: string;
  game_id: string;
  game_name: string;
  credits: number;
  cost: number;
  description: string;
  operation_type: string;
  is_active: boolean;
  created_by: string;
  created_by_id: string;
  created_by_username: string;
  updated_by_username?: string;
  updated_by?: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  purchased_at?: string | null;
}

export interface GetSerialCodesResponse {
  success: boolean;
  data?: {
    data: SerialCodeData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export const getSerialCodes = async (
  params: GetSerialCodesRequest
): Promise<GetSerialCodesResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      game_id,
      game_name,
      operation_type,
      status,
      is_active,
      created_by_username,
      serial_code_name,
      serial_code,
      purchased,
      all,
    } = params;
    const queryParams = new URLSearchParams();

    if (!all) {
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
    } else {
      queryParams.append('all', 'true');
    }

    if (search) {
      queryParams.append('search', search);
    }
    if (game_id) {
      queryParams.append('game_id', game_id);
    }
    if (game_name) {
      queryParams.append('game_name', game_name);
    }
    if (operation_type) {
      queryParams.append('operation_type', operation_type);
    }
    if (status) {
      queryParams.append('status', status);
    }
    if (is_active !== undefined) {
      queryParams.append('is_active', is_active.toString());
    }
    if (created_by_username) {
      queryParams.append('created_by_username', created_by_username);
    }
    if (serial_code_name) {
      queryParams.append('serial_code_name', serial_code_name);
    }
    if (serial_code) {
      queryParams.append('serial_code', serial_code);
    }
    if (purchased !== undefined) {
      queryParams.append('purchased', purchased.toString());
    }

    const response = await serialCodeAPI.get(
      `/admin/serial-code-top-ups?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        data: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: response.data.message || 'Serial codes fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch serial codes',
    };
  }
};

export interface GetSerialCodeByIdResponse {
  success: boolean;
  data?: SerialCodeData;
  message?: string;
  httpStatus?: number;
}

const isAbortLike = (error: unknown) => {
  const e = error as { code?: string; name?: string };
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError'
  );
};

export const getSerialCodeById = async (
  id: string,
  opts?: { signal?: AbortSignal; skipGlobal500ErrorToast?: boolean }
): Promise<GetSerialCodeByIdResponse> => {
  try {
    const response = await serialCodeAPI.get(
      `/admin/serial-code-top-ups/${id}`,
      {
        skipGlobal500ErrorToast: opts?.skipGlobal500ErrorToast === true,
        signal: opts?.signal,
      }
    );

    return {
      success: true,
      data: response.data,
      message: 'Serial code fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    return {
      success: false,
      httpStatus: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch serial code',
    };
  }
};

export interface SerialCodeStatsData {
  total: number;
  active: number;
  inactive: number;
}

export interface GetSerialCodeStatsResponse {
  success: boolean;
  data?: SerialCodeStatsData;
  message?: string;
}

export const getSerialCodeStats =
  async (): Promise<GetSerialCodeStatsResponse> => {
    try {
      const response = await serialCodeAPI.get(
        '/admin/serial-code-top-ups/stats'
      );

      return {
        success: true,
        data: response.data,
        message: 'Serial code stats fetched successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch serial code stats',
      };
    }
  };

export interface CreateSerialCodeRequest {
  game_id: string;
  serial_code: string;
  serial_code_name: string;
  credits: number;
  cost: number;
  description: string;
  is_active: boolean;
}

export interface CreateSerialCodeResponse {
  success: boolean;
  data?: SerialCodeData;
  message?: string;
}

export const createSerialCode = async (
  data: CreateSerialCodeRequest
): Promise<CreateSerialCodeResponse> => {
  try {
    const response = await serialCodeAPI.post('/admin/serial-code-top-ups', {
      game_id: data.game_id,
      serial_code: data.serial_code,
      serial_code_name: data.serial_code_name,
      credits: data.credits,
      cost: data.cost,
      description: data.description,
      is_active: data.is_active,
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Serial code created successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create serial code',
    };
  }
};

export interface UpdateSerialCodeRequest {
  id: string;
  serial_code: string;
  serial_code_name: string;
  credits: number;
  cost: number;
  description: string;
  is_active: boolean;
}

export interface UpdateSerialCodeResponse {
  success: boolean;
  data?: SerialCodeData;
  message?: string;
}

export const updateSerialCode = async (
  data: UpdateSerialCodeRequest
): Promise<UpdateSerialCodeResponse> => {
  try {
    const response = await serialCodeAPI.put(
      `/admin/serial-code-top-ups/${data.id}`,
      {
        serial_code: data.serial_code,
        serial_code_name: data.serial_code_name,
        credits: data.credits,
        cost: data.cost,
        description: data.description,
        is_active: data.is_active,
      }
    );

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Serial code updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update serial code',
    };
  }
};

export interface DeleteSerialCodeResponse {
  success: boolean;
  message?: string;
}

export const deleteSerialCode = async (
  id: string
): Promise<DeleteSerialCodeResponse> => {
  try {
    const response = await serialCodeAPI.delete(
      `/admin/serial-code-top-ups/${id}/soft`
    );

    return {
      success: true,
      message: response.data.message || 'Serial code deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete serial code',
    };
  }
};

export interface UploadSerialCSVRow {
  row: number;
  game_name: string;
  game_id?: string;
  serial_code: string;
  serial_code_name: string;
  credits: number;
  description: string;
  is_active: boolean;
  is_valid: boolean;
  error?: string;
}

export interface UploadSerialCSVResponse {
  success: boolean;
  data?: {
    success_count: number;
    failed_count: number;
    total_count: number;
    rows: UploadSerialCSVRow[];
  };
  message?: string;
}

export const uploadSerialCSV = async (
  file: File
): Promise<UploadSerialCSVResponse> => {
  try {
    const formData = new FormData();
    formData.append('csv_file', file);

    const response = await serialCodeAPI.post(
      '/admin/serial-code-top-ups/upload-csv',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'CSV uploaded successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to upload CSV file',
    };
  }
};

export default serialCodeAPI;
